/**
 * Error Boundary Component
 *
 * Catches React rendering errors and displays a fallback UI.
 * Also handles route errors when integrated with React Router.
 */

import { Component, type ReactNode } from 'react'
import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, RefreshCcw, Home, ArrowLeft } from 'lucide-react'
import { ErrorCodes, type AppErrorCode } from '@/shared/errors'

// ============================================
// Props and State types
// ============================================

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

// ============================================
// Error Boundary Class Component
// ============================================

/**
 * Class-based Error Boundary for catching React rendering errors
 *
 * Use this at the top level of your app to catch unhandled errors.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console (could be replaced with error reporting service)
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)

    // Call optional error handler
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.handleReset}
        />
      )
    }

    return this.props.children
  }
}

// ============================================
// Error Fallback UI
// ============================================

interface ErrorFallbackProps {
  error: Error | null
  resetError?: () => void
  showDetails?: boolean
}

/**
 * Default fallback UI for error states
 *
 * Can be used standalone or as part of ErrorBoundary.
 */
export function ErrorFallback({
  error,
  resetError,
  showDetails = false,
}: ErrorFallbackProps) {
  const handleRefresh = () => {
    if (resetError) {
      resetError()
    } else {
      window.location.reload()
    }
  }

  const handleGoHome = () => {
    window.location.href = '/'
  }

  return (
    <div className="flex min-h-[400px] items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Something went wrong</CardTitle>
          <CardDescription>
            An unexpected error occurred. Please try again or return to the home page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showDetails && error && (
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm font-mono text-muted-foreground break-all">
                {error.message}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={handleRefresh} className="flex-1">
              <RefreshCcw className="h-4 w-4" />
              Try Again
            </Button>
            <Button variant="outline" onClick={handleGoHome} className="flex-1">
              <Home className="h-4 w-4" />
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ============================================
// Route Error Boundary (for React Router)
// ============================================

/**
 * Route-level error component for React Router errorElement
 *
 * Handles both thrown responses (404, etc.) and regular errors.
 *
 * @example
 * ```tsx
 * const router = createBrowserRouter([
 *   {
 *     path: '/',
 *     element: <Layout />,
 *     errorElement: <RouteErrorBoundary />,
 *     children: [...]
 *   }
 * ])
 * ```
 */
export function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()

  // Handle React Router's thrown responses (404, etc.)
  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <span className="text-2xl font-bold text-muted-foreground">
                {error.status}
              </span>
            </div>
            <CardTitle>
              {getRouteErrorTitle(error.status)}
            </CardTitle>
            <CardDescription>
              {getRouteErrorMessage(error.status, error.statusText)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => navigate(-1)} variant="outline" className="flex-1">
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </Button>
              <Button onClick={() => navigate('/')} className="flex-1">
                <Home className="h-4 w-4" />
                Go Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Handle regular JavaScript errors
  const errorMessage = error instanceof Error ? error.message : 'Unknown error'

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle>Application Error</CardTitle>
          <CardDescription>
            Something went wrong while loading this page.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md bg-muted p-3">
            <p className="text-sm font-mono text-muted-foreground break-all">
              {errorMessage}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => window.location.reload()} className="flex-1">
              <RefreshCcw className="h-4 w-4" />
              Reload Page
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex-1">
              <Home className="h-4 w-4" />
              Go Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ============================================
// API Error Display Component
// ============================================

interface ApiErrorDisplayProps {
  code?: AppErrorCode | null
  message: string
  onRetry?: () => void
  className?: string
}

/**
 * Inline error display for API errors
 *
 * Use this in components to show API error states.
 *
 * @example
 * ```tsx
 * if (error) {
 *   return <ApiErrorDisplay message={error.message} onRetry={refetch} />
 * }
 * ```
 */
export function ApiErrorDisplay({
  code,
  message,
  onRetry,
  className,
}: ApiErrorDisplayProps) {
  const retryableCodes: AppErrorCode[] = [
    ErrorCodes.RATE_LIMITED,
    ErrorCodes.TIMEOUT,
    ErrorCodes.SERVICE_UNAVAILABLE,
  ]
  const isRetryable = code ? retryableCodes.includes(code) : false

  return (
    <div className={`rounded-lg border border-destructive/20 bg-destructive/5 p-4 ${className || ''}`}>
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <p className="text-sm text-destructive font-medium">
            {getErrorTitle(code)}
          </p>
          <p className="text-sm text-muted-foreground">
            {message}
          </p>
          {onRetry && isRetryable && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="mt-2"
            >
              <RefreshCcw className="h-3 w-3" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================
// Helper functions
// ============================================

function getRouteErrorTitle(status: number): string {
  switch (status) {
    case 404:
      return 'Page Not Found'
    case 401:
      return 'Unauthorized'
    case 403:
      return 'Access Denied'
    case 500:
      return 'Server Error'
    default:
      return 'Error'
  }
}

function getRouteErrorMessage(status: number, statusText?: string): string {
  switch (status) {
    case 404:
      return "The page you're looking for doesn't exist or has been moved."
    case 401:
      return 'You need to sign in to access this page.'
    case 403:
      return "You don't have permission to access this page."
    case 500:
      return 'The server encountered an error. Please try again later.'
    default:
      return statusText || 'An unexpected error occurred.'
  }
}

function getErrorTitle(code?: AppErrorCode | null): string {
  if (!code) return 'Error'

  switch (code) {
    case ErrorCodes.UNAUTHORIZED:
    case ErrorCodes.SESSION_EXPIRED:
      return 'Authentication Required'
    case ErrorCodes.FORBIDDEN:
      return 'Access Denied'
    case ErrorCodes.NOT_FOUND:
      return 'Not Found'
    case ErrorCodes.RATE_LIMITED:
      return 'Too Many Requests'
    case ErrorCodes.VALIDATION_ERROR:
    case ErrorCodes.INVALID_INPUT:
      return 'Validation Error'
    case ErrorCodes.SERVICE_UNAVAILABLE:
    case ErrorCodes.TIMEOUT:
      return 'Service Unavailable'
    default:
      return 'Error'
  }
}

// ============================================
// Exports
// ============================================

export default ErrorBoundary
